<template>
  <div class="dashboard">
    <!-- Hero header -->
    <header class="dashboard__hero">
      <div>
        <h1 class="al-display">Overview</h1>
        <p class="al-subtle" style="margin-top: 0.5rem">
          Executive summary &amp; recent activity
        </p>
      </div>
      <button type="button" class="al-btn al-btn--primary" @click="openSnapshotDrawer">
        <span class="material-symbols-outlined" style="font-size: 18px">add_box</span>
        Create Account Snapshots
      </button>
    </header>

    <!-- KPI bento grid -->
    <section class="dashboard__kpis">
      <article class="al-kpi">
        <span class="material-symbols-outlined al-kpi__watermark al-kpi__watermark--primary">
          account_balance_wallet
        </span>
        <h3 class="al-kpi__label">Total Income</h3>
        <div class="al-kpi__value al-kpi__value--positive">
          {{ formatCurrency(totalIncome) }}
        </div>
        <div class="al-kpi__delta al-kpi__delta--positive">
          <span class="material-symbols-outlined" style="font-size: 16px">trending_up</span>
          Cumulative this period
        </div>
      </article>

      <article class="al-kpi">
        <span class="material-symbols-outlined al-kpi__watermark al-kpi__watermark--tertiary">
          credit_card
        </span>
        <h3 class="al-kpi__label">Personal Expenses</h3>
        <div class="al-kpi__value">{{ formatCurrency(personalExpenses) }}</div>
        <div class="al-kpi__delta al-kpi__delta--negative">
          <span class="material-symbols-outlined" style="font-size: 16px">trending_down</span>
          Individual ledger
        </div>
      </article>

      <article class="al-kpi">
        <span class="material-symbols-outlined al-kpi__watermark al-kpi__watermark--tertiary">
          group
        </span>
        <h3 class="al-kpi__label">Shared Expenses</h3>
        <div class="al-kpi__value">{{ formatCurrency(sharedExpenses) }}</div>
        <div class="al-kpi__delta">
          <span class="material-symbols-outlined" style="font-size: 16px">group</span>
          Household ledger
        </div>
      </article>
    </section>

    <!-- Summary + charts grid -->
    <section class="dashboard__grid">
      <div class="dashboard__grid-left">
        <AccountSummaryTable />
      </div>
      <div class="dashboard__grid-right">
        <div class="al-card">
          <h2 class="al-title" style="margin: 0 0 1.25rem">Monthly Balance</h2>
          <n-spin :show="loading">
            <MonthlyAccountChart @mounted="onMonthlyDataLoaded" />
          </n-spin>
        </div>
        <div class="al-card">
          <h2 class="al-title" style="margin: 0 0 1.25rem">Yearly Trend</h2>
          <n-spin :show="loading">
            <YearlyAccountChart @mounted="onYearlyDataLoaded" />
          </n-spin>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { inject, ref, computed } from 'vue';
import { NSpin, useMessage } from 'naive-ui';
import { useIncomeStore } from '../stores/incomeStore';
import { useExpenseStore } from '../stores/expenseStore';
import AccountSummaryTable from '../components/AccountSummaryTable.vue';
import MonthlyAccountChart from '../components/MonthlyAccountChart.vue';
import YearlyAccountChart from '../components/YearlyAccountChart.vue';

const layout = inject('layout');
const message = useMessage();
const loading = ref(true);
let monthlyLoaded = false;
let yearlyLoaded = false;

const incomeStore = useIncomeStore();
const expenseStore = useExpenseStore();

incomeStore.fetchIncomes();
expenseStore.fetchExpenses();

const totalIncome = computed(() =>
  incomeStore.incomes.reduce((sum, income) => sum + income.amount, 0)
);

const personalExpenses = computed(() =>
  expenseStore.expenses
    .filter((e) => e.account_type_target === 'Individual')
    .reduce((sum, e) => sum + e.amount, 0)
);

const sharedExpenses = computed(() =>
  expenseStore.expenses
    .filter((e) => e.account_type_target === 'Shared')
    .reduce((sum, e) => sum + e.amount, 0)
);

const formatCurrency = (value) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value || 0);

const openSnapshotDrawer = () => {
  if (layout && layout.value) {
    layout.value.showSnapshotDrawer = true;
  } else {
    message.error('Drawer initialization failed');
  }
};

const checkAllLoaded = () => {
  if (monthlyLoaded && yearlyLoaded) loading.value = false;
};

const onMonthlyDataLoaded = () => {
  monthlyLoaded = true;
  checkAllLoaded();
};

const onYearlyDataLoaded = () => {
  yearlyLoaded = true;
  checkAllLoaded();
};
</script>

<style scoped>
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.dashboard__hero {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.5rem;
}

.dashboard__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
}

.dashboard__grid {
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: 2rem;
  align-items: start;
}

.dashboard__grid-right {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

@media (max-width: 1100px) {
  .dashboard__kpis {
    grid-template-columns: 1fr;
  }
  .dashboard__grid {
    grid-template-columns: 1fr;
  }
}
</style>
